import EASING_FUNCTIONS from '../math/easing';
import type { Easing } from '../math/easing';

export interface TweenProps {
    from?: number;
    to: number;
    duration?: number;
    delay?: number;
    easing?: Easing;
    onProgress?: Function;
    onStart?: Function;
    onComplete?: Function;
}


const defaultProps: TweenProps = {
    from: 0,
    to: 0,
    duration: 0,
    delay: 0,
    easing: 'linear',
}

class Tween {
    private _value: number;
    private _props: TweenProps = {...defaultProps};
    private _startTime: number = 0;


    private _rafID: number = 0;
    private _delayID: number = 0;    
    private _boundUpdate: () => void;

    constructor(defaultValue: number = 0) {
        this._value = defaultValue;
        this._boundUpdate = this._update.bind(this);
    }

    to(props: TweenProps): this {

        this._props = {
            ...defaultProps,
            from: this._value,                        
            ...props,
        }        

        this._value = this._props.from ?? this._value;
        
        this.stop();

        if(this._props.delay === 0) {
            this._start();
        } else {
            this._delayID = window.setTimeout(this._start.bind(this), this._props.delay);
        }
        
        return this;
    }

    _start(): void {
        this._startTime = Date.now();
        this._update();

        if(this._props.onStart) {
            this._props.onStart();
        }            
    }

    _update(): void {
        this._rafID = window.requestAnimationFrame(this._boundUpdate);
        const currentTime:number = Date.now() - this._startTime;
        const _duration = this._props.duration ?? 0;
        const _to = this._props.to;
        const _from = this._props.from ?? this._value;        
        const _easing = this._props.easing ?? 'linear';
        
        if(_duration > 0) {            
            const progress = currentTime / _duration;
            // 0 - 1でclamp
            if(progress <= 0) {                
                this._value = _from;
            }
            else if(progress >= 1) {
                this._value = _to;
            }
            else {                
                const easedProgress = EASING_FUNCTIONS[_easing]?.(progress) || 0; // easing                  
                this._value = _from * (1 - easedProgress) + _to * easedProgress; // lerp
            }
        } else {
            this._value = _to;
        }

        this._props.onProgress?.(this._value);

        if(currentTime >= _duration || this._props.duration === 0) {
            this.stop();
            this._props.onComplete?.();
        }        
    }

    stop(): void {        
        window.clearTimeout(this._delayID);                
        window.cancelAnimationFrame(this._rafID);
    }

    get value(): number {
        return this._value;
    }

    set value(value: number) {
        this._value = value;
    }
}

export default Tween;
